import moment from 'moment-timezone';
import { ApiClient, GET, POST } from 'Api';
import { SUPPORTED_INPUT_CAPTURES, CaptureInput, DataCapture } from 'CarePlans/Data';
import { TaskId } from 'Tasks/Data';
import { CarePlanActionStatusesResponseJSON } from './JSON/CarePlans';
import { dateToJSON, timestampToJSON } from './JSON/Shared';

export interface CarePlansAPI {
  getActionStatuses: (
    date: moment.Moment
  ) => Promise<CarePlanActionStatusesResponseJSON>,

  completeTaskAction: (
    taskId: TaskId, time: moment.Moment, input?: CaptureInput
  ) => Promise<void>,

  undoTaskAction: (
    date: moment.Moment,
    actionId: number
  ) => Promise<void>
}

export function makeApi(client: ApiClient): CarePlansAPI {
  return {
    getActionStatuses,
    completeTaskAction,
    undoTaskAction
  };

  function getActionStatuses(
    date: moment.Moment
  ): Promise<CarePlanActionStatusesResponseJSON> {
    const captures = SUPPORTED_INPUT_CAPTURES.join(',');
    return client.requestJSON(
      GET,
      {
        version: 3,
        path: `/care_plan_action_statuses/${dateToJSON(date)}` +
          `?supported_captures=${captures}`
      }
    );
  }

  function completeTaskAction(
    taskId: TaskId, time: moment.Moment, input?: CaptureInput
  ): Promise<void> {
    const data: CompleteTaskActionData = {
      task_id: taskId,
      time: timestampToJSON(time)
    };
    if (input) { data.inputs = captureInputToJSON(input); }
    return client.requestJSON(
      POST,
      { version: 3, path: '/care_plan_action_statuses/complete_task_action' },
      data
    );
  }

  function undoTaskAction(
    date: moment.Moment,
    actionId: number
  ): Promise<void> {
    return client.requestJSON(
      POST,
      {
        version: 3,
        path: `/care_plan_action_statuses/${dateToJSON(date)}/undo_task_action`
      },
      { action_id: actionId }
    );
  }
}

interface CompleteTaskActionData {
  task_id: TaskId
  time: number
  inputs?: CaptureInputJSON
}

type CaptureInputJSON = { pain_rating: number };

function captureInputToJSON(input: CaptureInput): CaptureInputJSON {
  switch (input.capture) {
    case DataCapture.PAIN_RATING:
      return { pain_rating: input.painRating };
    default: {
      const exhaustive: never = input.capture;
      return exhaustive;
    }
  }
}
